// © 2015-2021 WDNA - All Rights Reserved

import * as React from 'react';
import _ from 'lodash';

import { WidgetGrid, JarvisWidget } from 'wdna-components/lib';
import { initProcess, finishProcess } from 'wdna-components/lib/scheduler/actions';
import { removeTab } from 'wdna-components/lib/tabs';
import Loader from 'wdna-components/lib/loaders/local/Loader';
import UiValidate from 'wdna-components/lib/forms/validation/UiValidate';
import { messageError, messageInfo } from 'wdna-components/lib/helpers/notification';
import { getErrorMessage } from 'wdna-components/lib/helpers/response';

import { firmware } from '../../../requests';

import Form from './Form';
import ButtonsForm from '../ButtonsForm';

interface Props {
  rowId?: number;
  notify?: () => void;
}

interface State {
  loading: boolean;
  uploading: boolean;

  id?: number;
  file: string;
  ver: string;
}

export default class Edit extends React.Component<Props, State> {
  private pid: any;
  private mounted: boolean;
  private defaultValues: any;
  private fileId: any;
  private fileName: any;

  public constructor(props: Props) {
    super(props);

    this.pid = window.getStr();
    this.mounted = false;
    this.defaultValues = {};
    this.fileId = window.getStr();
    this.fileName = window.getStr();

    this.state = {
      loading: false,
      uploading: false,

      file: '',
      ver: '',
    };
  }

  public componentDidMount = (): void => {
    this.mounted = true;
    this.load();
  };

  public componentWillUnmount = (): void => {
    this.mounted = false;
  };

  public load = (): void => {
    const { rowId } = this.props || {};
    if (rowId !== undefined && !isNaN(rowId)) {
      this.loadingStart();
      firmware('get', String(rowId))
        ?.then((resp: any) => {
          if (this.mounted) {
            this.setState({ ...resp }, () => {
              this.defaultValues = _.cloneDeep(resp);
            });
          }
        })
        .catch((error: any) => {
          messageError('Error', getErrorMessage(error));
        })
        .finally(() => {
          this.loadingFinish();
        });
    }
  };

  public changeFileName = (): void => {
    const { fileId, fileName } = this;
    const file: any = $(`#${fileId}`).prop('files');

    if (!file[0]) {
      return;
    }

    const { name } = file[0];
    $(`#${fileName}`).val(name);
  };

  public handleUploadFile = (): void => {
    const formData: FormData = new FormData();

    const { fileId } = this;
    const file: any = $(`#${fileId}`).prop('files');

    if (!file[0]) {
      if (this.state.id === undefined || isNaN(this.state.id)) {
        messageError('Error', 'Please select a valid file');
        return;
      }
    } else {
      this.uploadingStart();
      formData.append('ufile', file[0]);
    }

    if (this.state.id !== undefined && !isNaN(this.state.id)) {
      formData.append('id', String(this.state.id));
    }
    formData.append('ver', this.state.ver);

    const options: any = { headers: { 'Content-Type': 'multipart/form-data' } };
    firmware('set', undefined, formData, options)
      ?.then(() => {
        messageInfo('Success', `Firmware successfully ${this.state.id ? 'edited' : 'created'}`, 5000);
        const { notify } = this.props || {};
        if (typeof notify === 'function') {
          notify();
        }
        window.store.dispatch(removeTab('FE'));
      })
      .catch((error: any) => {
        this.uploadingError(error);
      });
  };

  public uploadingStart = (): void => {
    this.setState({
      uploading: true,
    });
  };

  public uploadingError = (error: any): void => {
    if (error) {
      messageError('Error', getErrorMessage(error));
    } else {
      messageError('Error', 'Unknown error');
    }
    this.setState({
      uploading: false,
    });
  };

  public loadingStart = (): void => {
    this.setState({
      loading: true,
    });
    window.store.dispatch(initProcess(this.pid));
  };

  public loadingFinish = (): void => {
    this.setState({
      loading: false,
    });
    window.store.dispatch(finishProcess(this.pid));
  };

  public onReset = (): void => {
    this.setState((prevState: State) => ({
      ...prevState,
      ...this.defaultValues,
    }));
  };

  public submit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    this.loadingStart();
    this.handleUploadFile();
    this.loadingFinish();
  };

  public download = (): void => {
    const { file } = this.state;
    const { protocol } = location;
    const host: string = protocol.concat('//', window.location.hostname);
    const url: string = host.concat('/firmware/', file);
    fetch(url).then((response: Response) => {
      response.blob().then((blob: Blob) => {
        const href: string = window.URL.createObjectURL(blob);
        const a: HTMLAnchorElement = document.createElement('a');
        a.href = href;
        a.download = file;
        a.click();
      }).catch((error: any) => {
        messageError('Error', getErrorMessage(error));
      });
    }).catch((error: any) => {
      messageError('Error', getErrorMessage(error));
    });
  };

  public handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    this.changeFileName();
    const { value, name } = e.target;
    this.setState((prevState: State) => ({
      ...prevState,
      [name]: value,
    }));
  };

  public render(): JSX.Element {
    const { fileId, fileName } = this;
    return (
      <div id="content" className="content">
        <WidgetGrid>
          <div className="row">
            <article className="col-sm-12 col-md-12 col-lg-12">
              <JarvisWidget
                editbutton={false}
                togglebutton={false}
                colorbutton={false}
                deletebutton={false}
                custombutton={false}
                fullscreenbutton={false}
              >
                <div>
                  <div className="widget-body">
                    <Loader show={this.state.loading} overlay={false} />
                    <UiValidate>
                      <form className="smart-form" onSubmit={this.submit} method="post" encType="multipart/form-data">
                        <header>
                          <h2>
                            <span className="widget-icon" style={{ padding: '8px' }}>
                              <i className="fa fa-edit" />
                            </span>
                            Firmware {this.state.id ? `Edit: ${this.state.id}` : 'New'}
                          </h2>
                        </header>
                        <Form
                          fileId={fileId}
                          fileName={fileName}
                          onChange={this.handleChange}
                          formValues={this.state}
                          download={this.download}
                        />
                        <ButtonsForm formValues={this.state} onReset={this.onReset} />
                      </form>
                    </UiValidate>
                  </div>
                </div>
              </JarvisWidget>
            </article>
          </div>
        </WidgetGrid>
      </div>
    );
  }
}
